

import React from 'react';
import {SortableContainer, SortableElement, SortEnd} from 'react-sortable-hoc';
import arrayMove from 'array-move';
import {action, observable} from "mobx";
import {inject, observer} from "mobx-react";
import levenshtein from "js-levenshtein";
import * as _ from "lodash";
import {ProgressBar} from "./ProgressBar";
import {RecipientInfo} from "../../../../libs/RecipientInfo";
import {UI} from "../../../UI";
import {Global} from "../../../../libs/Global";
import {EMode} from "../../../../libs/Mode";
import UIStore from "../../../stores/UIStore";
import RStore from "../../../stores/RStore";
import PStore from "../../../stores/PStore";

interface IPuzzleBoardProps {
    word:string; // puzzle we are solving for
    lastSavedWord?:string; // last state before user gave up
    puzzleCompleteHandler:()=>void; // handler to be called when the puzzle is complete

    R_STORE?:RStore;
    P_STORE?:PStore;
}

/**
 * Puzzle board for user to interact with on descrambling
 */
@inject('R_STORE', 'P_STORE')
@observer
export class PuzzleBoard extends React.Component<IPuzzleBoardProps, any> {

    @observable private items:ISortableItem[] = [];

    // starting point
    @observable private startWord:string;
    @observable private startLevenshtienDistance:number;

    // previous word
    @observable private previousWord:string;
    @observable private previousLevenshtienDistance:number;
    private previousTime:number;

    // current word
    @observable private currentWord:string;
    @observable private currentLevenshtienDistance:number;


    // progress
    @observable private temp:ETemperature;

    constructor(props:any) {

        super(props);

        // if last saved word is defined
        if (_.isNull(this.props.lastSavedWord) == false) {
            // then continue from that point
            this.currentWord = this.props.lastSavedWord!;
        }
        // otherwise start fresh
        else {
            // get scrambled version of word passed in
            this.currentWord = this.scrambleString(this.props.word);
        }

        // get levenshtien distance we are starting from
        this.currentLevenshtienDistance = this.getLevenshteinDistance(this.props.word, this.currentWord);
        this.previousTime = Date.now();

        // keep starting word info
        this.startWord = this.currentWord;
        this.startLevenshtienDistance = this.currentLevenshtienDistance

        // we are just starting so initialize with some dummy values
        this.previousWord = this.currentWord;
        this.previousLevenshtienDistance = Number.MAX_SAFE_INTEGER;

        // get last message
        this.items = this.makeSortableItemsFromString(this.currentWord);

        // just starting
        this.temp = ETemperature.Start;

        // if not testing
        if (Global.mode != EMode.Testing) {
            // save the current state of play in the database
            RecipientInfo.updateRecordAsPlayed(UI.R_STORE.r.recipientRecord.id, 0, this.currentWord);
        }

        // if word was found already
        if (this.startLevenshtienDistance == 0) {
            this.temp = ETemperature.End;

            // call the puzzle complete handler set
            this.props.puzzleCompleteHandler();

        }

    }

    public render() {
        return (
            <>
                <div className="col-row">
                    <div className="col-left">
                        <p>It's time to unwrap your gift. To do this, solve the puzzle below {String.fromCodePoint(0x1F575)}! Don't worry, you'll get some encouragement along the way {String.fromCodePoint(0x1F9B8)}!</p>

                    </div>
                    <div className="col-right">
                        <p>To solve the puzzle, you'll need to unscramble the word. Take a close look at the clue below {String.fromCodePoint(0x1F50D)}. That should be enough to get you to the finish line {String.fromCodePoint(0x1F3C1)}.</p>


                    </div>
                </div>
                <div className="col-row">
                    <div className="col-left">
                        <div className="form-label"><label className="form-label" htmlFor="progress">Progress</label></div>
                        <p>
                            <ProgressBar
                                maxValue={this.startLevenshtienDistance}
                                currentValue={this.currentLevenshtienDistance}
                                message={this.getTemperatureMessage()} />
                        </p>

                    </div>
                    <div className="col-right" style={{background: '#fffF9E', margin: '10px', border: '1px solid lightgrey'}}>
                        <div style={{padding: '10px'}}>
                            <div className="form-label"><label className="form-label" htmlFor="clue" style={{fontFamily: '"Courier New", Courier, monospace'}}>Clue</label></div>
                            <p style={{fontFamily: '"Courier New", Courier, monospace'}}>{"\""+this.props.P_STORE!.p.puzzleRecord.clue+"\""}</p>
                        </div>

                    </div>
                </div>



                <div style={{textAlign:"center"}}>
                    <SortableList
                        items={this.items}
                        onSortEnd={(collection:any)=>{this.onSortEnd_Handler(collection)}}
                        helperClass="tile-dragging"
                        axis="x"
                    />
                </div>
            </>

        );
    }

    /**
     * Handler for when sort has ended
     *
     * @param sortInfo
     */
    @action
    private onSortEnd_Handler (sortInfo:SortEnd)  {

        // get copy of sortable item
        let itemsCopy:ISortableItem[] = this.items.slice();

        // move item from old position to new position
        this.items = arrayMove(itemsCopy, sortInfo.oldIndex, sortInfo.newIndex);

        // save previous word info
        this.previousWord = this.currentWord;
        this.previousLevenshtienDistance = this.currentLevenshtienDistance;

        // save current word and calculate current levenshtien distance
        this.currentWord = this.makeStringFromSortableItems(this.items);
        this.currentLevenshtienDistance = this.getLevenshteinDistance(this.currentWord, this.props.word);

        // if word is found, the levenshtin distance should be 0
        if (this.currentLevenshtienDistance == 0) {
            this.temp = ETemperature.End;

            // call the puzzle complete handler set
            this.props.puzzleCompleteHandler();

        }
        // if levenshtien distance is less than previous, then we got closer to answer
        else if (this.currentLevenshtienDistance < this.previousLevenshtienDistance) {
            this.temp = ETemperature.Warmer;
        }
        // if levenshtien distance is same as previous, then we are working on it
        else if (this.currentLevenshtienDistance == this.previousLevenshtienDistance) {
            this.temp = ETemperature.Same;
        }
        // if levenshtien distance greater than previous, then we are farther away from answer
        else if (this.currentLevenshtienDistance > this.previousLevenshtienDistance) {
            this.temp = ETemperature.Colder;
        }

        // calculate the number of seconds played, and save the current moment as the previous time
        let previousTime = this.previousTime;
        let currentTime = Date.now();
        let secondsPlayed = Math.round((currentTime - previousTime) / 1000);
        this.previousTime = currentTime;

        // if not testing
        if (Global.mode != EMode.Testing) {
            // save the current state of play in the database
            RecipientInfo.updateRecordAsPlayed(UI.R_STORE.r.recipientRecord.id, secondsPlayed, this.currentWord);
        }
    };

    /**
     * Make array of sortable items from given word
     *
     * @param word
     */
    private makeSortableItemsFromString(word:string): ISortableItem[] {

        // list of items
        let items:ISortableItem[] = [];

        // for each character of word, make sortable item
        for (let i:number=0; i < word.length; i++) {
            items.push({label: word[i], value: i.toString()})
        }

        return items;
    }

    /**
     * Make string from an array of sortable items
     *
     * @param items
     */
    private makeStringFromSortableItems(items:ISortableItem[]): string {

        // list of items
        let word:string = ""

        // for each character of word, make sortable item
        for (let i:number=0; i < items.length; i++) {
            word = word + items[i].label;
        }

        return word;
    }

    /**
     * Takes an initial string and scrambles it
     *
     * @param word
     */
    private scrambleString(word:string):string {

        // get word as array
        let wordChars:string[] = word.split("");

        for (let i:number = 0; i < word.length; i++) {
            wordChars = (arrayMove(wordChars, i, Math.floor(Math.random()*word.length)));
        }

        return wordChars.join("");
    }

    /**
     * Given two words, get the levenshtien distance between them
     *
     * @param word1
     * @param word2
     */
    private getLevenshteinDistance(word1:string, word2:string):number {
        return levenshtein(word1, word2);
    }

    /**
     * Retrieves a random temperature message for variety
     */
    private getTemperatureMessage():string {

        switch (this.temp) {
            case ETemperature.Start:
                return PuzzleBoard.tempMessageStart[Math.floor(Math.random()*(PuzzleBoard.tempMessageStart.length))];
            case ETemperature.Colder:
                return PuzzleBoard.tempMessageColder[Math.floor(Math.random()*(PuzzleBoard.tempMessageColder.length))];
            case ETemperature.Same:
                return PuzzleBoard.tempMessageSame[Math.floor(Math.random()*(PuzzleBoard.tempMessageSame.length))];
            case ETemperature.Warmer:
                return PuzzleBoard.tempMessageWarmer[Math.floor(Math.random()*(PuzzleBoard.tempMessageWarmer.length))];
            case ETemperature.End:
                return PuzzleBoard.tempMessageEnd[Math.floor(Math.random()*(PuzzleBoard.tempMessageEnd.length))];
        }
    }

    private static tempMessageStart:string[] = [
        "Let's get going! "+ String.fromCodePoint(0x1F449),
        "You got this! "+ String.fromCodePoint(0x1F4AA),
        "Giddy up! "+ String.fromCodePoint(0x1F920),
    ];

    private static tempMessageColder:string[] = [
        "You're getting colder... " + String.fromCodePoint(0x2744),
        "You're getting colder... " + String.fromCodePoint(0x1F976),
        "You're getting colder... " + String.fromCodePoint(0x1F630),
    ];

    private static tempMessageSame:string[] = [
        "Keep it up Einstein! " + String.fromCodePoint(0x1F913),
        "Keep on the throttle! "+ String.fromCodePoint(0x1F680),
        "Watching you is magical! "+ String.fromCodePoint(0x1F9D9),
    ];

    private static tempMessageWarmer:string[] = [
        "You're getting warmer... " + String.fromCodePoint(0x1F525),
        "You're getting warmer... " + String.fromCodePoint(0x1F44F),
        "You're getting warmer... " + String.fromCodePoint(0x1F336),
    ];

    private static tempMessageEnd:string[] = [
        "You did it! " + String.fromCodePoint(0x1F308),
        "Time to celebrate! "+ String.fromCodePoint(0x1F973),
        "That was amazing! "+ String.fromCodePoint(0x1F92F),
    ];
}


/**
 * Enum showing how recipient is doing in solving puzzle
 */
enum ETemperature {
    Warmer= "warmer",
    Same = "same",
    Colder = "colder",
    Start = "start",
    End = "end"
}

/**
 * Interface for a sortable item
 */
interface ISortableItem {
    value:string;
    label:string;
}

/**
 * The object representing the element that is to be sorted
 */
// @ts-ignore
const SortableItem = SortableElement(({value}) => <div className="tile">{value.label}</div>);

/**
 * The object representing the entire list of things that are to be sorted
 */
// @ts-ignore
const SortableList = SortableContainer(({items}) => {
    return (
        <div className="puzzle-container">
            {items.map((value:any, index:any) => (
                <SortableItem key={`item-${value.value}`} index={index} value={value} />
            ))}
        </div>
    );
});